/* SRC cart runtime — loaded on every page from theme.liquid.
   Lives here (not inline in snippets) because snippet scripts only execute
   when the initial page load happens to render them: markup injected by
   drawer re-renders never runs its <script> tags, which left the upsell and
   plan-pill dead on pages that loaded with an empty or complete cart. */

// Product handles get renamed in the Shopify admin (core-longevity-drink ->
// core, and so on). Shopify redirects the URLs but cart line handles come back
// as the CURRENT handle, so matching a literal handle silently stops working.
// Match on these lists instead, and keep them in step with
// snippets/src-handles.liquid.
const SRC_CORE_HANDLES = ['core', 'core-longevity-drink', 'src-core'];
const SRC_FUEL_HANDLES = ['fuel-electrolyte-drink-mix', 'fuel', 'src-fuel'];
const srcIsCore = (h) => SRC_CORE_HANDLES.includes(h);
const srcIsFuel = (h) => SRC_FUEL_HANDLES.includes(h);

/* 1. Clean-realm fetch for SRC cart mutations.
   Appstle Bundles wraps window.fetch and rewrites theme add-to-cart payloads
   from its (hidden) volume-widget state — forcing subscription plans and
   bundle attributes onto one-time adds. A blank same-origin iframe still
   holds the browser's native fetch, out of the wrapper's reach. Appstle's
   own widget keeps the wrapped fetch it needs, so the combo bundle keeps
   working. */
(function () {
  var frame = null;
  window.srcCartFetch = function (url, opts) {
    // Absolute URL: the iframe realm may not inherit this page's base, so
    // relative paths like /cart/add.js can fail to parse there.
    var abs;
    try { abs = new URL(url, window.location.href).href; } catch (e) { abs = url; }
    try {
      if (!frame || !frame.contentWindow) {
        frame = document.createElement('iframe');
        frame.setAttribute('aria-hidden', 'true');
        frame.tabIndex = -1;
        frame.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden;';
        document.documentElement.appendChild(frame);
      }
      return frame.contentWindow.fetch.call(frame.contentWindow, abs, opts).catch(function () {
        return window.fetch(abs, opts);
      });
    } catch (e) {
      return window.fetch(abs, opts);
    }
  };
})();

/* 2. Per-line subscription control (<src-cart-plan>, rendered by
   snippets/src-cart-plan.liquid in the drawer and on the cart page). */
(function () {
  if (customElements.get('src-cart-plan')) return;
  customElements.define('src-cart-plan', class extends HTMLElement {
    connectedCallback() {
      // Delegated: safe even if this element upgrades before children parse
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-plan]');
        if (btn && !this.classList.contains('is-loading')) this.swap(btn.dataset.plan);
      });
    }

    async swap(planId) {
      this.classList.add('is-loading');
      const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
      const inDrawer = !!this.closest('cart-drawer');
      const payload = {
        id: this.dataset.key,
        quantity: Number(this.dataset.quantity) || 1,
        selling_plan: planId ? Number(planId) : null,
      };
      if (inDrawer) payload.sections = ['cart-drawer'];
      try {
        const res = await (window.srcCartFetch || window.fetch)(root + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload),
        });
        if (!res.ok) throw new Error('change failed');
        const data = await res.json();
        if (inDrawer && data.sections && data.sections['cart-drawer']) {
          const doc = new DOMParser().parseFromString(data.sections['cart-drawer'], 'text/html');
          const incoming = doc.querySelector('.drawer__inner');
          const current = document.querySelector('#CartDrawer .drawer__inner');
          if (incoming && current) current.innerHTML = incoming.innerHTML;
        } else {
          window.location.reload();
        }
      } catch (err) {
        window.location.reload();
      }
    }
  });
})();

/* 3b. Bundle converter: when the cart holds CORE + FUEL as singles, one tap
   swaps them for THE BUNDLE (one-time today; on its monthly plan once the
   product carries one in Appstle). Flavors ride along as line properties. */
(function () {
  if (window.__srcBundleConvertWired) return;
  window.__srcBundleConvertWired = true;
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('button[data-bundle-convert]');
    if (!btn || btn.disabled) return;
    const cartFetch = window.srcCartFetch || window.fetch;
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const inDrawer = !!btn.closest('#CartDrawer');
    btn.classList.add('is-loading');
    btn.disabled = true;
    try {
      const cart = await cartFetch(root + 'cart.js').then((r) => r.json());
      // Never touch Appstle combo-widget lines — they're components of a
      // server-priced bundle (the drawer markup may be stale when clicked).
      const swappable = cart.items.filter((i) => !(i.properties && i.properties['_appstle-bb-id']));
      const core = swappable.find((i) => srcIsCore(i.handle));
      const fuel = swappable.find((i) => srcIsFuel(i.handle));
      if (!core || !fuel) throw new Error('halves missing');
      const props = { 'CORE flavor': core.variant_title || '', 'FUEL flavor': fuel.variant_title || '' };
      const item = { id: Number(btn.dataset.bundleVariant), quantity: 1, properties: props };
      if (btn.dataset.mode === 'sub' && btn.dataset.plan) item.selling_plan = Number(btn.dataset.plan);
      const addRes = await cartFetch(root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [item] }) });
      if (!addRes.ok) throw new Error('add failed');
      const updates = {};
      updates[core.key] = core.quantity - 1;
      updates[fuel.key] = fuel.quantity - 1;
      const upBody = { updates: updates };
      if (inDrawer) upBody.sections = ['cart-drawer'];
      const data = await cartFetch(root + 'cart/update.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(upBody) }).then((r) => r.json());
      if (inDrawer && data && data.sections && data.sections['cart-drawer']) {
        const doc = new DOMParser().parseFromString(data.sections['cart-drawer'], 'text/html');
        const incoming = doc.querySelector('.drawer__inner');
        const current = document.querySelector('#CartDrawer .drawer__inner');
        if (incoming && current) current.innerHTML = incoming.innerHTML;
        const fresh = await cartFetch(root + 'cart.js').then((r) => r.json());
        const bubble = document.querySelector('.cart-count-bubble span[aria-hidden="true"]');
        if (bubble) bubble.textContent = fresh.item_count;
      } else {
        window.location.reload();
      }
    } catch (err) {
      btn.classList.remove('is-loading');
      btn.disabled = false;
    }
  });
})();

/* 2b. Drawer empty-state sync. Appstle's combo widget refreshes the drawer
   after its own add by re-rendering the inner markup, but it can't touch the
   host <cart-drawer> element — so a page that loaded with an empty cart keeps
   the stale `is-empty` class, and Dawn's CSS hides the header and the whole
   items form (.is-empty .cart__contents): the drawer opens visually blank.
   The class is derived state; recompute it from the rendered markup after
   every subtree change (the empty-state block only exists in server markup
   when the cart is actually empty). Idempotent alongside Dawn's own toggles
   and the manual removes in our PDP/builder add paths. */
(function () {
  function wire() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer) return;
    var sync = function () {
      var inner = drawer.querySelector('.drawer__inner');
      if (!inner) return;
      drawer.classList.toggle('is-empty', !!inner.querySelector('.drawer__inner-empty'));
    };
    sync();
    new MutationObserver(sync).observe(drawer, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wire);
  } else {
    wire();
  }
})();

/* 2c. Sampler quick-add ([data-sampler-add] buttons on the homepage sampler
   strip and the PDP cross-sell strips). Delegated at document level like all
   cart JS: strip markup can arrive via section re-renders. */
(function () {
  if (window.__srcSamplerAddWired) return;
  window.__srcSamplerAddWired = true;
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('button[data-sampler-add]');
    if (!btn || btn.disabled) return;
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const drawer = document.querySelector('cart-drawer');
    const body = new FormData();
    body.append('id', btn.dataset.variantId);
    body.append('quantity', 1);
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      body.append('sections', drawer.getSectionsToRender().map((s) => s.id).join(','));
      body.append('sections_url', window.location.pathname);
      if (typeof drawer.setActiveElement === 'function') drawer.setActiveElement(btn);
    }
    btn.disabled = true;
    btn.classList.add('is-adding');
    try {
      const res = await (window.srcCartFetch || window.fetch)(root + 'cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || 'add failed');
      if (drawer && data.sections && typeof drawer.renderContents === 'function') {
        drawer.classList.remove('is-empty');
        drawer.renderContents(data);
      } else {
        window.location.href = root + 'cart';
      }
    } catch (err) {
      window.location.href = root + 'cart';
    } finally {
      btn.disabled = false;
      btn.classList.remove('is-adding');
    }
  });
})();

/* 3. System-completion upsell (markup from snippets/src-cart-upsell.liquid).
   Delegated at document level so the Add button survives every drawer
   re-render. When THE BUNDLE product carries a monthly selling plan
   (data-bundle-* attributes render), one tap swaps the single item for the
   real bundle on subscription; until then it adds the missing half
   one-time. */
(function () {
  if (window.__srcCartUpsellWired) return;
  window.__srcCartUpsellWired = true;
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-upsell-add]');
    if (!btn || btn.disabled) return;
    const cartFetch = window.srcCartFetch || window.fetch;
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const inDrawer = !!btn.closest('#CartDrawer');
    btn.classList.add('is-loading');
    btn.disabled = true;
    const renderDrawer = async (data) => {
      if (inDrawer && data && data.sections && data.sections['cart-drawer']) {
        const doc = new DOMParser().parseFromString(data.sections['cart-drawer'], 'text/html');
        const incoming = doc.querySelector('.drawer__inner');
        const current = document.querySelector('#CartDrawer .drawer__inner');
        if (incoming && current) current.innerHTML = incoming.innerHTML;
        const cart = await cartFetch(root + 'cart.js').then((r) => r.json());
        const bubble = document.querySelector('.cart-count-bubble span[aria-hidden="true"]');
        if (bubble) bubble.textContent = cart.item_count;
        return true;
      }
      return false;
    };
    // The module's flavor select (missing half) wins over the default
    // first-variant baked into the button's data attributes.
    const flavorSel = btn.closest('src-cart-upsell')?.querySelector('[data-upsell-flavor]');
    const flavorOpt = flavorSel && flavorSel.options[flavorSel.selectedIndex];
    try {
      if (btn.dataset.bundleVariant) {
        // Bundle-assembly: swap the single item already in the cart for
        // THE BUNDLE (on its monthly plan when the button carries one,
        // one-time otherwise), carrying both flavors as properties.
        const cart = await cartFetch(root + 'cart.js').then((r) => r.json());
        const half = cart.items.find((i) => (srcIsCore(i.handle) || srcIsFuel(i.handle)) && !(i.properties && i.properties['_appstle-bb-id']));
        const props = {};
        if (half) props[srcIsCore(half.handle) ? 'CORE flavor' : 'FUEL flavor'] = half.variant_title || '';
        const targetTitle = (flavorOpt && (flavorOpt.dataset.title || flavorOpt.textContent.trim())) || btn.dataset.targetTitle;
        if (btn.dataset.targetHandle && targetTitle) {
          props[srcIsCore(btn.dataset.targetHandle) ? 'CORE flavor' : 'FUEL flavor'] = targetTitle;
        }
        const item = { id: Number(btn.dataset.bundleVariant), quantity: 1, properties: props };
        if (btn.dataset.bundlePlan) item.selling_plan = Number(btn.dataset.bundlePlan);
        const addRes = await cartFetch(root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [item] }) });
        if (!addRes.ok) throw new Error('add failed');
        let data = null;
        if (half) {
          const changeBody = { id: half.key, quantity: half.quantity - 1 };
          if (inDrawer) changeBody.sections = ['cart-drawer'];
          data = await cartFetch(root + 'cart/change.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(changeBody) }).then((r) => r.json());
        }
        if (!(await renderDrawer(data))) window.location.reload();
      } else {
        const plainId = (flavorSel && Number(flavorSel.value)) || Number(btn.dataset.variantId);
        const body = { items: [{ id: plainId, quantity: 1 }] };
        if (inDrawer) body.sections = ['cart-drawer'];
        const res = await cartFetch(root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
        if (!res.ok) throw new Error('add failed');
        const data = await res.json();
        if (!(await renderDrawer(data))) window.location.reload();
      }
    } catch (err) {
      btn.classList.remove('is-loading');
      btn.disabled = false;
    }
  });
})();
